import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDiscoveryClient } from '../api/context';
import { leadsKey } from './leads';
import type {
  WidenOption,
  LeadEnrichment,
  ScrapeJobLogEntry,
  ScrapeJobPoll,
  ScrapeParams,
  SearchHistoryEntry,
  SkipBreakdown,
} from '../api/types';

/**
 * Starts a scrape job. Returns { jobId } that callers can pass to
 * useScrapeJobPoll to subscribe to live progress.
 */
export function useStartScrape() {
  const client = useDiscoveryClient();
  return useMutation({
    mutationFn: (params: ScrapeParams) => client.scrape.start(params),
  });
}

interface ScrapeJobState {
  status: 'idle' | 'running' | 'done' | 'error';
  logs: ScrapeJobLogEntry[];
  leads: LeadEnrichment[];
  saved: number;
  checked: number;
  cacheHits: number;
  apiCalls: number;
  preFiltered: number;
  skipped: number;
  skipBreakdown: SkipBreakdown | null;
  stage: string | null;
  /** Null until the run finishes, and null when no floor could be relaxed. */
  widenAnalysis: WidenOption[] | null;
  finishedAt: string | null;
  error: string | null;
}

const EMPTY_STATE: ScrapeJobState = {
  status: 'idle',
  logs: [],
  leads: [],
  saved: 0,
  checked: 0,
  cacheHits: 0,
  apiCalls: 0,
  preFiltered: 0,
  skipped: 0,
  skipBreakdown: null,
  stage: null,
  widenAnalysis: null,
  finishedAt: null,
  error: null,
};

/**
 * Polls the discovery server for incremental scrape-job state.
 *
 * Caller passes a jobId (null means no active job). We poll every 1.2s with
 * the `since` cursor so the server only sends new logs/leads. We stop
 * polling on done/error.
 *
 * On done, invalidates the target folder's leads cache so the table refetches.
 */
export function useScrapeJobPoll(jobId: string | null, folderPublicId: string | null): ScrapeJobState {
  const client = useDiscoveryClient();
  const qc = useQueryClient();
  const [state, setState] = useState<ScrapeJobState>(EMPTY_STATE);
  const logIndex = useRef(0);
  const leadIndex = useRef(0);
  const seenJobId = useRef<string | null>(null);

  // Reset when jobId changes
  useEffect(() => {
    if (jobId !== seenJobId.current) {
      seenJobId.current = jobId;
      logIndex.current = 0;
      leadIndex.current = 0;
      setState(jobId ? { ...EMPTY_STATE, status: 'running' } : EMPTY_STATE);
    }
  }, [jobId]);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;

    async function tick() {
      if (cancelled || !jobId) return;
      try {
        const poll: ScrapeJobPoll = await client.jobs.poll(
          jobId,
          logIndex.current,
          leadIndex.current,
        );
        if (cancelled) return;
        const newLogs = poll.logs;
        const newLeads = poll.newLeads;
        logIndex.current += newLogs.length;
        leadIndex.current += newLeads.length;
        setState((prev) => ({
          ...prev,
          status: poll.status === 'running' ? 'running' : poll.status,
          logs: newLogs.length ? [...prev.logs, ...newLogs] : prev.logs,
          leads: newLeads.length ? [...prev.leads, ...newLeads] : prev.leads,
          saved: poll.saved,
          checked: poll.checked,
          cacheHits: poll.cacheHits,
          apiCalls: poll.apiCalls,
          preFiltered: poll.preFiltered,
          skipped: poll.skipped ?? 0,
          skipBreakdown: poll.skipBreakdown ?? null,
          stage: poll.stage ?? null,
          widenAnalysis: poll.widenAnalysis ?? null,
          finishedAt: poll.finishedAt,
          error: null,
        }));

        if (poll.status === 'done' || poll.status === 'error') {
          if (folderPublicId) {
            qc.invalidateQueries({ queryKey: leadsKey(folderPublicId) });
          }
          return; // stop polling
        }
      } catch (err) {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          status: 'error',
          error: err instanceof Error ? err.message : 'Polling failed',
        }));
        return;
      }
      // schedule next tick
      setTimeout(tick, 1200);
    }

    tick();
    return () => {
      cancelled = true;
    };
  }, [jobId, client, folderPublicId, qc]);

  return state;
}

/** Friendly summary of a finished job's "why it stopped" message. */
export function lastDoneMessage(state: ScrapeJobState): string | null {
  if (state.status !== 'done' && state.status !== 'error') return null;
  for (let i = state.logs.length - 1; i >= 0; i--) {
    const log = state.logs[i];
    if (log && (log.type === 'done' || log.type === 'error')) return log.msg;
  }
  return null;
}

export function useStopScrape() {
  const client = useDiscoveryClient();
  return useMutation({
    mutationFn: (jobId: string) => client.jobs.stop(jobId),
  });
}

export function useExportCsv() {
  const client = useDiscoveryClient();
  return useMutation({
    mutationFn: (folderPublicId: string) => client.leads.exportCsv(folderPublicId),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'leads.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  });
}

function parseCsvText(text: string): Record<string, string>[] {
  const lines: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (ch === '"') {
      if (inQuotes && text[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if ((ch === '\n' || (ch === '\r' && text[i + 1] === '\n')) && !inQuotes) {
      lines.push(current);
      current = '';
      if (ch === '\r') i++;
    } else {
      current += ch;
    }
  }
  if (current.trim()) lines.push(current);

  if (lines.length < 2) return [];
  const splitRow = (row: string): string[] => {
    const cells: string[] = [];
    let cell = '';
    let q = false;
    for (let i = 0; i < row.length; i++) {
      const ch = row[i]!;
      if (ch === '"') {
        if (q && row[i + 1] === '"') { cell += '"'; i++; }
        else q = !q;
      } else if (ch === ',' && !q) {
        cells.push(cell);
        cell = '';
      } else {
        cell += ch;
      }
    }
    cells.push(cell);
    return cells;
  };
  const headers = splitRow(lines[0]!).map((h) => h.trim());
  return lines.slice(1).map((line) => {
    const values = splitRow(line);
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => { if (values[i] !== undefined) obj[h] = values[i]!; });
    return obj;
  });
}

const NUM_FIELDS = new Set([
  'followers', 'following', 'totalLikes', 'videoCount', 'avgViews', 'lastPostedDaysAgo',
]);

function csvRowToCreator(row: Record<string, string>): LeadEnrichment | null {
  const username = row['username']?.trim();
  if (!username) return null;
  const creator: Record<string, unknown> = { username };
  for (const [key, val] of Object.entries(row)) {
    if (key === 'username' || key === 'pipeline' || key === 'dmSentAt' || key === 'savedAt' || key === 'notes') continue;
    if (!val || val === '—') continue;
    creator[key] = NUM_FIELDS.has(key) ? Number(val) || 0 : val;
  }
  return creator as LeadEnrichment;
}

export function useImportCsv() {
  const client = useDiscoveryClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ folderPublicId, file }: { folderPublicId: string; file: File }) => {
      const text = await file.text();
      const rows = parseCsvText(text);
      const creators = rows.map(csvRowToCreator).filter(Boolean) as LeadEnrichment[];
      if (creators.length === 0) throw new Error('No valid rows found in CSV');
      return client.leads.saveBatch(folderPublicId, creators);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: leadsKey(variables.folderPublicId) });
    },
  });
}

export function useJobsList() {
  const client = useDiscoveryClient();
  return useQuery({
    queryKey: ['jobs-list'],
    queryFn: () => client.jobs.list(),
    refetchInterval: 5000,
  });
}

/**
 * Key for "is a job running in this folder". Exported because anything that
 * STARTS a job in the current folder has to invalidate it — the query only
 * refetches on mount, so without a nudge the progress panel doesn't notice a
 * job that began while it was already on screen.
 */
export const activeJobKey = (folderPublicId: string | null) => ['active-job', folderPublicId];

export function useActiveJob(folderPublicId: string | null) {
  const client = useDiscoveryClient();
  return useQuery({
    queryKey: activeJobKey(folderPublicId),
    queryFn: () => client.folders.activeJob(folderPublicId!),
    enabled: !!folderPublicId,
    refetchOnMount: 'always',
    staleTime: 0,
  });
}

export function useSearchHistory(folderPublicId: string | null) {
  const client = useDiscoveryClient();
  return useQuery({
    queryKey: ['search-history', folderPublicId],
    queryFn: () => client.folders.searchHistory(folderPublicId!),
    enabled: !!folderPublicId,
  });
}
